// Google Tag Manager / GA4 funnel tracking for the SPA.
//
// GTM is loaded from JS (not an inline <script> in index.html) because the CSP
// in public/serve.json only allows external https scripts. Consent Mode v2
// defaults to "denied" for everything before GTM loads; the CookieYes banner
// (a tag inside the GTM container) updates consent once the user decides. A
// decision already made on sepaheld.com is restored via ConsentBridge.
//
// Never pass personal data (email, names, company) to trackEvent. The only
// identifier sent is the pseudonymous customer id as GA4 User-ID (`user_id`),
// set via identifyAnalyticsUser() on login/registration; like everything else
// it only reaches Google after analytics consent. Support "log in as customer"
// sessions are never identified and send no custom events (internal traffic).

import {GTM_CONFIG} from '../../config/config';
import {CUSTOMER_KEY} from '../../constants/constants';
import {initConsentBridge} from './ConsentBridge';

const GTM_SCRIPT_ID = 'sepaheld-gtm';

const ONBOARDING_STARTED_KEY = 'analytics_onboarding_started';
const ONBOARDING_CHECKOUT_KEY = 'analytics_onboarding_checkout';
const TRACKED_ONCE_PREFIX = 'analytics_once_';
const ONBOARDING_CONTEXT_KEY = 'analytics_onboarding_context';
const SUPPORT_SESSION_KEY = 'analytics_support_session';

// Every parameter any event uses. Each push resets the ones it does not set,
// otherwise GTM's merged data-layer state would leak e.g. `erp` from an earlier
// event into a later one.
const EVENT_PARAM_KEYS = [
    'method',
    'reason',
    'step_name',
    'step_number',
    'erp',
    'erp_status',
    'gocardless_status',
    'plan_name',
    'plan_type',
    'billing_period',
    'value',
    'currency',
    'discount_code',
    'action',
    'status',
    'source',
    'link_type',
];

export const OnboardingSteps = {
    'Connect Lexoffice': {step_name: 'connect_erp', step_number: 1},
    'Connect GoCardless': {step_name: 'connect_gocardless', step_number: 2},
    'Payment Plan': {step_name: 'select_plan', step_number: 3},
};

export const resolveGtmId = (hostname = window.location.hostname) =>
    GTM_CONFIG.hosts.includes(hostname) ? GTM_CONFIG.containerId : '';

const dataLayer = () => {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
};

// GTM only understands consent commands pushed as an `arguments` object.
function gtag() {
    // eslint-disable-next-line prefer-rest-params
    dataLayer().push(arguments);
}

// Support/help entry points are tracked globally: any element with
// data-analytics-support="<source>", plus plain links to the support mailbox,
// the help center and the feedback board. Hovers count after a short dwell so
// "almost asked for help" is visible too.
const SUPPORT_LINK_PATTERNS = [
    [/^mailto:support@/i, 'email'],
    [/insigh\.to\//i, 'feedback_board'],
    [/sepaheld\.com\/f_a_q/i, 'help_center'],
];
const SUPPORT_HOVER_DWELL_MS = 400;
let interactionTrackingInitialized = false;

const findSupportTarget = (node) => {
    const element = node?.closest?.('[data-analytics-support], a[href]');
    if (!element) {
        return null;
    }
    const source = element.getAttribute('data-analytics-support');
    if (source) {
        return {element, source, link_type: element.getAttribute('data-analytics-link-type') || 'email'};
    }
    const href = element.getAttribute('href') || '';
    const match = SUPPORT_LINK_PATTERNS.find(([pattern]) => pattern.test(href));
    return match ? {element, source: 'link', link_type: match[1]} : null;
};

export const initInteractionTracking = () => {
    if (interactionTrackingInitialized) {
        return;
    }
    interactionTrackingInitialized = true;

    const hovered = new WeakSet();
    let pendingHover;

    document.addEventListener('click', (event) => {
        const target = findSupportTarget(event.target);
        if (target) {
            trackEvent('support_click', {source: target.source, link_type: target.link_type});
        }
    }, true);

    document.addEventListener('mouseover', (event) => {
        const target = findSupportTarget(event.target);
        if (!target || hovered.has(target.element) || pendingHover?.element === target.element) {
            return;
        }
        clearTimeout(pendingHover?.timer);
        const timer = setTimeout(() => {
            hovered.add(target.element);
            pendingHover = undefined;
            trackEvent('support_hover', {source: target.source, link_type: target.link_type});
        }, SUPPORT_HOVER_DWELL_MS);
        pendingHover = {element: target.element, timer};
    }, true);

    document.addEventListener('mouseout', (event) => {
        if (pendingHover && !pendingHover.element.contains(event.relatedTarget)) {
            clearTimeout(pendingHover.timer);
            pendingHover = undefined;
        }
    }, true);
};

export const isSupportSession = () => {
    try {
        return window.localStorage.getItem(SUPPORT_SESSION_KEY) === '1';
    } catch (error) {
        return false;
    }
};

const storedCustomerId = () => {
    try {
        return window.sessionStorage.getItem(CUSTOMER_KEY) || window.localStorage.getItem(CUSTOMER_KEY) || undefined;
    } catch (error) {
        return undefined;
    }
};

// `user_id` and `traffic_type` stay in the GTM data model (they are not in
// EVENT_PARAM_KEYS), so every later event and page view carries them. GTM maps
// them onto the Google tag / GA4 event tag (see ANALYTICS.md).
const pushIdentity = (customerId, supportSession, event) => {
    try {
        dataLayer().push({
            ...(event ? {event} : {}),
            user_id: !supportSession && customerId ? String(customerId) : undefined,
            traffic_type: supportSession ? 'internal' : undefined,
        });
    } catch (error) {
        // Tracking must never break the app.
    }
};

// Called whenever credentials are stored (login, registration, passkey,
// support login). `supportSession` marks staff acting as the customer.
export const identifyAnalyticsUser = (customerId, {supportSession = false} = {}) => {
    try {
        if (supportSession) {
            window.localStorage.setItem(SUPPORT_SESSION_KEY, '1');
        } else {
            window.localStorage.removeItem(SUPPORT_SESSION_KEY);
        }
    } catch (error) {
        // ignore
    }
    pushIdentity(customerId, supportSession, 'user_identified');
};

// Called on logout: later events are anonymous again.
export const clearAnalyticsUser = () => {
    try {
        window.localStorage.removeItem(SUPPORT_SESSION_KEY);
    } catch (error) {
        // ignore
    }
    pushIdentity(undefined, false);
};

export const initAnalytics = () => {
    initInteractionTracking();
    const gtmId = resolveGtmId();
    if (!gtmId || document.getElementById(GTM_SCRIPT_ID)) {
        return;
    }

    gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'granted',
        wait_for_update: 2000,
    });
    gtag('set', 'ads_data_redaction', true);
    gtag('set', 'url_passthrough', true);
    initConsentBridge(gtag);
    // Before gtm.js, so the Google tag already sees who is logged in.
    pushIdentity(storedCustomerId(), isSupportSession());

    dataLayer().push({'gtm.start': Date.now(), event: 'gtm.js'});

    const script = document.createElement('script');
    script.id = GTM_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
    document.head.appendChild(script);
};

export const trackEvent = (event, params = {}) => {
    if (isSupportSession()) {
        return;
    }
    try {
        const payload = {event};
        EVENT_PARAM_KEYS.forEach((key) => {
            payload[key] = undefined;
        });
        dataLayer().push({...payload, ...params});
    } catch (error) {
        // Tracking must never break the app.
    }
};

// Fires the event only once per browser (e.g. "ERP connected" should not be
// re-counted every time the onboarding page is reloaded).
export const trackEventOnce = (onceKey, event, params = {}) => {
    const key = TRACKED_ONCE_PREFIX + onceKey;
    try {
        if (window.localStorage.getItem(key)) {
            return;
        }
        window.localStorage.setItem(key, '1');
    } catch (error) {
        // Storage unavailable: fall through and track anyway.
    }
    trackEvent(event, params);
};

// Which ERP the customer picked and how far the connections got. Persisted so
// events after the Stripe redirect (checkout_cancelled, onboarding_complete)
// still carry it.
export const setOnboardingContext = (context) => {
    try {
        const current = getOnboardingContext();
        window.localStorage.setItem(ONBOARDING_CONTEXT_KEY, JSON.stringify({...current, ...context}));
    } catch (error) {
        // ignore
    }
};

export const getOnboardingContext = () => {
    try {
        return JSON.parse(window.localStorage.getItem(ONBOARDING_CONTEXT_KEY) || '{}');
    } catch (error) {
        return {};
    }
};

export const trackOnboardingEvent = (event, params = {}) =>
    trackEvent(event, {...getOnboardingContext(), ...params});

// Tracks a user action by the outcome of its backend call: `<event>` with
// status success|error. Returns the original promise unchanged.
export const trackAction = (promise, event, params = {}) => {
    if (!promise || typeof promise.then !== 'function') {
        return promise;
    }
    promise.then(
        () => trackEvent(event, {...params, status: 'success'}),
        () => trackEvent(event, {...params, status: 'error'}),
    );
    return promise;
};

// Activation after onboarding: payment condition → mandate → invoice →
// payment → payout. Automatic backend steps are never seen as they happen, so
// the app reports a milestone the first time its data shows it (once per
// customer and browser). Existing customers report theirs on their next visit.
export const ActivationMilestones = {
    paymentConditionSet: {event: 'activation_payment_condition_set', step_number: 1},
    firstMandateRequested: {event: 'activation_first_mandate_requested', step_number: 2},
    firstMandateActive: {event: 'activation_first_mandate_active', step_number: 3},
    firstInvoiceTransferred: {event: 'activation_first_invoice_transferred', step_number: 4},
    firstPaymentCollected: {event: 'activation_first_payment_collected', step_number: 5},
    firstPayout: {event: 'activation_first_payout', step_number: 6},
};

export const trackActivationMilestone = (milestone) => {
    const customerId = storedCustomerId();
    if (!milestone || !customerId) {
        return;
    }
    trackEventOnce(`${milestone.event}_${customerId}`, milestone.event, {step_number: milestone.step_number});
};

// Milestones derived from data the dashboard loads anyway.
export const trackActivationFromStatistics = (statistics) => {
    if (statistics?.invoicesTotal > 0) {
        trackActivationMilestone(ActivationMilestones.firstInvoiceTransferred);
    }
    if (statistics?.invoicesCompleted > 0) {
        trackActivationMilestone(ActivationMilestones.firstPaymentCollected);
    }
};

export const trackActivationFromMandateStats = (stats) => {
    if (!(stats?.total > 0)) {
        return;
    }
    trackActivationMilestone(ActivationMilestones.firstMandateRequested);
    if (stats?.completed > 0) {
        trackActivationMilestone(ActivationMilestones.firstMandateActive);
    }
};

export const trackActivationFromPayouts = (payouts) => {
    if (Array.isArray(payouts) && payouts.length > 0) {
        trackActivationMilestone(ActivationMilestones.firstPayout);
    }
};

export const trackOnboardingStepView = (stage) => {
    const step = OnboardingSteps[stage];
    if (step) {
        trackOnboardingEvent('onboarding_step_view', step);
    }
};

export const markOnboardingStarted = () => {
    try {
        if (!window.localStorage.getItem(ONBOARDING_STARTED_KEY)) {
            window.localStorage.setItem(ONBOARDING_STARTED_KEY, '1');
            trackOnboardingEvent('onboarding_start');
        }
    } catch (error) {
        trackEvent('onboarding_start');
    }
};

export const rememberCheckoutPlan = (planParams) => {
    try {
        window.localStorage.setItem(ONBOARDING_CHECKOUT_KEY, JSON.stringify(planParams));
    } catch (error) {
        // ignore
    }
};

// Called once the app knows the customer is onboarded. Only fires if this
// browser went through onboarding, so existing customers are not counted.
export const trackOnboardingCompletedIfPending = () => {
    try {
        if (!window.localStorage.getItem(ONBOARDING_STARTED_KEY)) {
            return;
        }
        const plan = JSON.parse(window.localStorage.getItem(ONBOARDING_CHECKOUT_KEY) || '{}');
        const context = getOnboardingContext();
        window.localStorage.removeItem(ONBOARDING_STARTED_KEY);
        window.localStorage.removeItem(ONBOARDING_CHECKOUT_KEY);
        window.localStorage.removeItem(ONBOARDING_CONTEXT_KEY);
        trackEvent('onboarding_complete', {...context, ...plan});
    } catch (error) {
        // ignore
    }
};
